import { Maybe } from '@/types';
import { OfficeInfoType } from '@/services/useFacilities';
import { AvailableSlot, NamedAttribute } from '@/graphql/__generated/sdk';
import { BffBrandType, FacilityBrand } from '@/types/generated';
import { getBffAvailabilityByLocation } from '@/services/getBffAvailabilityByLocation';

export const getAttributeByName = (
	officeInfo: Maybe<OfficeInfoType>,
	name: string
): NamedAttribute | undefined | null => {
	return officeInfo?.attributes?.find((attr) => {
		return attr && attr.name === name;
	});
};

export const getIsScheduleTypeOpen = (
	officeInfo: Maybe<OfficeInfoType>,
	facilityBrand: FacilityBrand | BffBrandType
): boolean => {
	return facilityBrand === FacilityBrand.Lovet && getAttributeByName(officeInfo, 'Schedule Type')?.value === 'Open';
};

export function filterSlotsUniqueByTime(availableSlots: AvailableSlot[], limit: number) {
	if (!availableSlots) return [];
	const slotsByDate: Record<string, AvailableSlot[]> = {};

	availableSlots.forEach((slot) => {
		if (slot) {
			if (!slotsByDate[slot.date]) {
				slotsByDate[slot.date] = [];
			}
			const isDuplicate = slotsByDate[slot.date].some((s) => s.date === slot.date && s.time === slot.time);
			if (!isDuplicate) {
				slotsByDate[slot.date].push(slot);
			}
		}
	});
	// Limit the number of slots per date to the specified limit
	Object.keys(slotsByDate).forEach((date) => {
		slotsByDate[date] = slotsByDate[date].slice(0, limit);
	});
	return slotsByDate;
}

export function getAvailableSlotsByDate(availableSlots: AvailableSlot[], limit: number, todaysDate: string) {
	const filteredSlotsUniqueByTime = filterSlotsUniqueByTime(availableSlots, limit) as Record<string, AvailableSlot[]>;

	// If there are slots for today, set isToday to true
	const isToday = filteredSlotsUniqueByTime[todaysDate]?.length > 0;

	const slotsByDateAsKeys: Record<string, AvailableSlot[]> = {};
	Object.values(filteredSlotsUniqueByTime)
		.flat()
		.forEach((slot) => {
			if (slot) {
				const dateKey = slot.date;
				if (!slotsByDateAsKeys[dateKey]) {
					slotsByDateAsKeys[dateKey] = [];
				}
				slotsByDateAsKeys[dateKey].push(slot);
			}
		});
	return {
		slots: slotsByDateAsKeys,
		isToday,
	};
}

export type TFetchTimesReturnType = {
	date: string;
	slots: AvailableSlot[];
	isToday: boolean;
};

export const fetchTimes = async ({
	brand,
	facilityId,
	url,
	appName,
	appVersion,
}: {
	brand: BffBrandType;
	facilityId: string;
	url: string;
	appName: string;
	appVersion: string;
}): Promise<TFetchTimesReturnType> => {
	const todaysDateLong = new Date();
	const todaysDate = todaysDateLong.toISOString().split('T')[0];

	try {
		const facilityIdShort = facilityId.replace(/^LV_/, ''); // getBffAvailabilityByLocation expects facilityId without 'LV_' instead of facility code
		const availabilityData = await getBffAvailabilityByLocation(brand, facilityIdShort, url, appName, appVersion);
		const tomorrowsDateLong = new Date(todaysDateLong);
		tomorrowsDateLong.setDate(todaysDateLong.getDate() + 1);
		const tomorrowsDate = tomorrowsDateLong.toISOString().split('T')[0];

		if (availabilityData?.availabilityByLocation?.availableSlots) {
			const availableSlotsByDate = getAvailableSlotsByDate(
				availabilityData.availabilityByLocation.availableSlots as AvailableSlot[],
				5,
				todaysDate
			);
			const whichDate = availableSlotsByDate.isToday ? todaysDateLong : tomorrowsDateLong;
			const formattedDateString = whichDate?.toLocaleDateString('en-US', {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
			});

			return {
				slots: availableSlotsByDate.isToday
					? availableSlotsByDate.slots[todaysDate]
					: availableSlotsByDate.slots[tomorrowsDate],
				isToday: availableSlotsByDate.isToday,
				date: formattedDateString ?? '',
			};
		}
	} catch (error) {
		console.error('Error fetching times:', error);
	}
	return {
		slots: [],
		isToday: true,
		date: '',
	};
};

export function formatDisplayTime(time: string): string {
	const [hours, minutes] = time.split(':');
	const formattedHours = parseInt(hours, 10) % 12 || 12; // Convert to 12-hour format
	const ampm = parseInt(hours, 10) < 12 ? 'AM' : 'PM';
	return `${formattedHours}:${minutes} ${ampm}`;
}
